<script lang="ts">
  /**
   * The sheet behind the round profile button: who you are (a name and one
   * line — signed in, synced), then the game's own rows (`OptionRow`s in
   * `OptionSection`s, passed as children): what the coins are, best scores,
   * how to play, settings, install.
   *
   * A frame only. Which rows there are, and what each one does, is the
   * game's; the shell draws the header and owns open, close and Back
   * (`Sheet`).
   */
  import type { Snippet } from 'svelte';

  import Avatar from './Avatar.svelte';
  import OptionsHeader from './OptionsHeader.svelte';
  import Sheet from './Sheet.svelte';

  interface Props {
    readonly open: boolean;
    /** The dialog's accessible name ("Profile"). */
    readonly label: string;
    /** The player's name, or the game's word for a guest. */
    readonly name: string;
    readonly detail?: string | undefined;
    /** One letter for the avatar; `null` draws a generic person. */
    readonly initial?: string | null;
    /** The player's picture (TwinTen ADR 0073), drawn in place of the letter. */
    readonly avatar?: string | null | undefined;
    readonly onclose: () => void;
    readonly children: Snippet;
    readonly nameTestid?: string | undefined;
    readonly testid?: string | undefined;
  }
  const {
    open,
    label,
    name,
    detail,
    initial = null,
    avatar,
    onclose,
    children,
    nameTestid,
    testid,
  }: Props = $props();
</script>

{#snippet face()}
  {#if avatar !== undefined && avatar !== null}
    <Avatar {name} src={avatar} {initial} size="md" tone="accent" />
  {:else if initial !== null && initial.length > 0}
    <span>{initial}</span>
  {:else}
    <svg viewBox="0 0 24 24" width="20" height="20">
      <path
        d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4.5 20c.6-3.8 3.7-6 7.5-6s6.9 2.2 7.5 6"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  {/if}
{/snippet}

{#snippet header()}
  <OptionsHeader title={name} {detail} icon={face} {onclose} titleTestid={nameTestid} />
{/snippet}

<Sheet {open} title={label} {onclose} {header} {testid}>
  {@render children()}
</Sheet>
